<template>
    <slot :loading="loading" :result="result" :packs="packs" :selectedPack="selectedPack" :openPrices="openPrices" :openPacks="openPacks" :bookNow="bookNow"></slot>
</template>

<script>
import { defineComponent, ref, onBeforeMount, onBeforeUnmount } from 'vue';
import { uniqBy, sortBy } from 'lodash';

import eventBus from './utils/event-bus';
import { getSeriesById } from '../services/inventory';
import { proxyGet } from '../utils/proxy';

export default defineComponent({
    name: 'SeriesPackHeader',
    props: {
        primary: {
            type: Boolean,
            required: false,
            default: false,
        },
        initialPacks: {
            type: Array,
            required: false,
            default: () => [],
        },
        seriesId: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const loading = ref(true);
        const packs = ref(props.initialPacks);
        const selectedPack = ref(null);
        const result = ref({});

        const processResults = () => {
            if (!selectedPack.value) {

                if(packs.value === null) {
                    return;
                }

                let events = packs?.value?.map(pack => {
                    return pack.events;
                });

                // flatten it
                events = [].concat(...events);

                // remove duplicates
                events = uniqBy(events, 'id');

                // sort by start day
                events = sortBy(events, 'startDate');

                result.value = {
                    title: 'All Concerts',
                    suffix: null,
                    events,
                }
            } else {
                result.value = selectedPack.value;
            }
        }

        const openPrices = () => {
            eventBus.emit('pricesPopup:open');
        }

        const openPacks = () => {
            eventBus.emit('packsPopup:open');
        }

        const bookNow = () => {
            if (!props.primary) {
                eventBus.emit('bookNow');
                return;
            }

            if (selectedPack.value.is_create_your_own) {
                window.location.href = `${AppConfig.boDomain.replace(/^\/|\/$/g, '')}/booking/package/flex/${selectedPack.value.package_id}`;
            } else {
                window.location.href = `${AppConfig.boDomain.replace(/^\/|\/$/g, '')}/booking/package/${selectedPack.value.package_id}`;
            }
        }

        const handlePackChange = pack => {
            selectedPack.value = pack;
            processResults();
        }

        const applyPacks = value => {
            packs.value = Array.isArray(value) ? value : [];

            if(packs.value.length === 1) {
                selectedPack.value = packs.value[0];
            }

            if (props.primary) {
                setTimeout(() => {
                    eventBus.emit('seriesPacks', packs.value);
                }, 0);
            }
        }

        const hydratePackEvents = async value => {
            const eventIds = Array.from(new Set(
                value.flatMap(pack => (pack.events ?? []).map(event => event.id)).filter(Boolean),
            ));

            if (eventIds.length === 0) {
                return value;
            }

            const response = await proxyGet('/events', {
                ids: eventIds.join(','),
                select: 'title,url,slug,publishedRelativePath,startDate,image',
                depth: 2,
                limit: eventIds.length,
            });
            const eventsById = new Map((response.docs ?? []).map(event => [event.id, event]));

            return value.map(pack => ({
                ...pack,
                events: (pack.events ?? []).map(event => eventsById.get(event.id) ?? event),
            }));
        }

        const finishLoading = () => {
            processResults();

            eventBus.on('packChanged', handlePackChange);
            eventBus.on('bookNow', bookNow);

            loading.value = false;
        }

        onBeforeMount(() => {
            if (props.initialPacks.length > 0) {
                applyPacks(props.initialPacks);
                hydratePackEvents(props.initialPacks)
                    .then(applyPacks, () => undefined)
                    .finally(finishLoading);
                return;
            }

            getSeriesById(props.seriesId)
                .then(series => applyPacks(series?.doc?.theme?.settings?.package_days))
                .finally(finishLoading);
        });

        onBeforeUnmount(() => {
            eventBus.off('packChanged', handlePackChange);
            eventBus.off('bookNow', bookNow);
        });

        return {
            loading,
            packs,
            selectedPack,
            result,
            openPrices,
            openPacks,
            bookNow,
        }
    },
});
</script>
